"use client";

import { c as _c } from "react/compiler-runtime";
import { type FC, type PropsWithChildren, type ReactNode, type RefAttributes, createContext, isValidElement, use } from "react";
import { ToggleButton as AriaToggleButton, ToggleButtonGroup as AriaToggleButtonGroup, type ToggleButtonGroupProps, type ToggleButtonProps } from "react-aria-components";
import { cx, sortCx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
export const styles = sortCx({
  common: {
    root: ["group/button-group inline-flex h-max cursor-pointer items-center bg-primary font-semibold whitespace-nowrap text-secondary shadow-skeumorphic ring-1 ring-primary outline-brand transition duration-100 ease-linear ring-inset",
    // Hover and focus styles
    "hover:bg-primary_hover hover:text-secondary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
    // Disabled styles
    "disabled:cursor-not-allowed disabled:bg-primary disabled:text-disabled",
    // Selected styles
    "selected:bg-active selected:text-secondary_hover selected:disabled:bg-disabled_subtle"].join(" "),
    icon: "pointer-events-none text-fg-quaternary transition-[inherit] group-hover/button-group:text-fg-quaternary_hover group-disabled/button-group:text-fg-disabled_subtle"
  },
  sizes: {
    sm: {
      root: "gap-1.5 px-3.5 py-2 text-sm not-last:pr-[calc(calc(var(--spacing)*3.5)+1px)] first:rounded-l-lg last:rounded-r-lg data-icon-leading:pl-3 data-icon-only:p-2",
      icon: "size-5"
    },
    md: {
      root: "gap-1.5 px-4 py-2.5 text-sm not-last:pr-[calc(calc(var(--spacing)*4)+1px)] first:rounded-l-lg last:rounded-r-lg data-icon-leading:pl-3.5 data-icon-only:px-3",
      icon: "size-5"
    },
    lg: {
      root: "gap-2 px-4.5 py-2.5 text-md not-last:pr-[calc(calc(var(--spacing)*4.5)+1px)] first:rounded-l-lg last:rounded-r-lg data-icon-leading:pl-4 data-icon-only:p-3",
      icon: "size-5"
    }
  }
});
type ButtonSize = keyof typeof styles.sizes;
const ButtonGroupContext = createContext<{
  size: ButtonSize;
}>({
  size: "md"
});
interface ButtonGroupItemProps extends ToggleButtonProps, RefAttributes<HTMLButtonElement> {
  iconLeading?: FC<{
    className?: string;
  }> | ReactNode;
  iconTrailing?: FC<{
    className?: string;
  }> | ReactNode;
  onClick?: () => void;
  className?: string;
}
export const ButtonGroupItem = t0 => {
  const $ = _c(29);
  let IconLeading;
  let IconTrailing;
  let children;
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      iconLeading: IconLeading,
      iconTrailing: IconTrailing,
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = IconLeading;
    $[2] = IconTrailing;
    $[3] = children;
    $[4] = className;
    $[5] = otherProps;
  } else {
    IconLeading = $[1];
    IconTrailing = $[2];
    children = $[3];
    className = $[4];
    otherProps = $[5];
  }
  const context = use(ButtonGroupContext);
  if (!context) {
    throw new Error("ButtonGroupItem must be used within a ButtonGroup component");
  }
  const {
    size
  } = context;
  const isIcon = (IconLeading || IconTrailing) && !children;
  const t1 = isIcon ? true : undefined;
  const t2 = IconLeading ? true : undefined;
  const t3 = styles.sizes[size];
  let t4;
  if ($[6] !== className || $[7] !== t3.root) {
    t4 = cx(styles.common.root, t3.root, className);
    $[6] = className;
    $[7] = t3.root;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== IconLeading || $[10] !== size) {
    t5 = isReactComponent(IconLeading) && <IconLeading className={cx(styles.common.icon, styles.sizes[size].icon)} />;
    $[9] = IconLeading;
    $[10] = size;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== IconLeading) {
    t6 = isValidElement(IconLeading) && IconLeading;
    $[12] = IconLeading;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== IconTrailing || $[15] !== size) {
    t7 = isReactComponent(IconTrailing) && <IconTrailing className={cx(styles.common.icon, styles.sizes[size].icon)} />;
    $[14] = IconTrailing;
    $[15] = size;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== IconTrailing) {
    t8 = isValidElement(IconTrailing) && IconTrailing;
    $[17] = IconTrailing;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== children || $[20] !== otherProps || $[21] !== t1 || $[22] !== t2 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <AriaToggleButton {...otherProps} data-icon-only={t1} data-icon-leading={t2} className={t4}>{t5}{t6}{children}{t7}{t8}</AriaToggleButton>;
    $[19] = children;
    $[20] = otherProps;
    $[21] = t1;
    $[22] = t2;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
interface ButtonGroupProps extends Omit<ToggleButtonGroupProps, "orientation">, RefAttributes<HTMLDivElement> {
  size?: ButtonSize;
  className?: string;
}
export const ButtonGroup = t0 => {
  const $ = _c(16);
  let children;
  let className;
  let otherProps;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      size: t1,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[5] !== size) {
    t2 = {
      size
    };
    $[5] = size;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cx("relative z-0 inline-flex w-max -space-x-px rounded-lg shadow-xs", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== otherProps || $[11] !== t3) {
    t4 = <AriaToggleButtonGroup selectionMode="single" className={t3} {...otherProps}>{children}</AriaToggleButtonGroup>;
    $[9] = children;
    $[10] = otherProps;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <ButtonGroupContext.Provider value={t2}>{t4}</ButtonGroupContext.Provider>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};